import { pipe, values, chain, prop, filter, pluck } from 'ramda';

import { getFlexSearchInstance } from './utils.ts';

export default function createSearchTypeahead(pois, poiSearch, lang) {
  const suggestedKeywordsSearch = createSuggestedKeywordsSearch(pois, lang);

  function queryPois(query, limit) {
    const matches = poiSearch({ query, limit });
    const withSuggestions = poiSearch({ query, suggest: true, limit });
    const matchedIds = matches.map(poi => poi.poiId);
    const filteredSuggestions = withSuggestions.filter(poi => matchedIds.indexOf(poi.poiId) === -1);
    return matches.concat(filteredSuggestions);
  }

  const query = (query, limit) => {
    const suggestedKeywords = suggestedKeywordsSearch.search({ query, limit });
    const queryLengthUnderLimit = query.length < 3;
    const shouldQueryPois = !queryLengthUnderLimit && suggestedKeywords.length;
    const poisLimit = limit - suggestedKeywords.length;
    const pois = shouldQueryPois ? queryPois(query, poisLimit) : [];
    return { keywords: suggestedKeywords, pois };
  };

  const addKeyword = keyword => {
    suggestedKeywordsSearch.add(keyword);
  };

  const updatePOIs = updatedPois => {
    const allPois = suggestedKeywordsSearch.getAllPois();
    const mergedPois = { ...allPois };
    for (const poi of updatedPois) {
      mergedPois[poi.poiId] = poi;
    }
    suggestedKeywordsSearch.update(mergedPois);
  };

  return { query, addKeyword, updatePOIs };
}

function buildKeywordsAndIndex(pois, lang) {
  const categories = extractParentCategories(pois);
  const poisKeywords = pipe(values, chain(prop('keywords')), filter(prop('isUserSearchable')), pluck('name'))(pois);
  const poiNames = pipe(values, pluck('name'))(pois);
  const poiStaticNames = pipe(
    values,
    filter(poi => poi.staticName && poi.staticName !== poi.name),
    pluck('staticName'),
  )(pois);
  const allPotentialKeywords = [...categories, ...poisKeywords, ...poiNames, ...poiStaticNames];
  const keywords = Array.from(new Set(allPotentialKeywords));
  const index = getFlexSearchInstance({ lang, type: 'typeahead' });
  keywords.forEach((keyword, i) => index.add(i, keyword));
  return { keywords, index };
}

function createSuggestedKeywordsSearch(pois, lang) {
  let allPois = pois;
  let { keywords, index } = buildKeywordsAndIndex(pois, lang);

  const search = queryParams => {
    const ids = index.search(queryParams);
    const results = ids.map(index => keywords[index]);
    return rankByExactMatch(results, queryParams.query);
  };
  const add = newKeyword => {
    keywords.push(newKeyword);
    index.add(keywords.length - 1, newKeyword);
  };
  const update = newPois => {
    allPois = newPois;
    const built = buildKeywordsAndIndex(newPois, lang);
    keywords = built.keywords;
    index = built.index;
  };
  const getAllPois = () => allPois;
  return { search, add, update, getAllPois };
}

function extractParentCategories(pois) {
  return Object.values(pois)
    .map(poi => poi.category)
    .map(fullCategory => fullCategory.split('.'))
    .map(subcategories => subcategories[0]);
}

/**
 * Promotes a keyword exactly matching the query (case-insensitive) to the top,
 * keeping FlexSearch order for the rest.
 */
function rankByExactMatch(results, query) {
  const normalizedQuery = (query ?? '').toLowerCase();
  if (!normalizedQuery) {
    return results;
  }
  return results
    .map((keyword, index) => ({ keyword, index, tier: keyword.toLowerCase() === normalizedQuery ? 0 : 1 }))
    .sort((a, b) => a.tier - b.tier || a.index - b.index)
    .map(entry => entry.keyword);
}
